import { useLingui } from '@lingui/react/macro';
import { Image } from '@/routes/-components/image';
import { Suspense } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';
import { Expand, ExpandGroup, EXPAND_STEP_ROOMY } from '../expand';
import { formatBandFigure } from '../figure-format';
import { AppImpactIllustration } from '@worldcoo/ui/illustrations/animated/app-impact-illustration';

/** Its own component, so the Suspense boundary wraps the number alone. */
function ProjectsFunded() {
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	const { language } = useTranslation();
	return <>{formatBandFigure(language, stats.projectsFunded)}</>;
}

/** The right column splits 373 + 248 around the 24px gutter so both columns come to the same height. */
export function Hero() {
	const { t } = useLingui();
	return (
		// `lead`: this hero hydrates at once, with no Suspense boundary to wait behind (`ExpandGroup`).
		<ExpandGroup lead={1} step={EXPAND_STEP_ROOMY} asChild>
			<section className="mx-auto grid max-w-[1440px] gap-6 px-6 pt-6 pb-16 md:pb-24 lg:grid-cols-[899fr_437fr] xl:px-10">
				{/* A gradient, not the hand-off's flat 20%: on a phone the subline lands over white cloth and washed out. */}
				<Expand index={0} asChild>
					<div className="relative flex min-h-[420px] items-end overflow-hidden rounded-3xl lg:min-h-[645px]">
						<Image
							src="/redesign/about/hero-auditorium.jpg"
							alt={t`Rows of auditorium seats draped with Worldcoo covers`}
							height={1290}
							aspectRatio={899 / 645}
							objectFit="cover"
							className="absolute inset-0 size-full object-cover!"
						/>
						<div
							aria-hidden
							className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 via-45% to-black/10"
						/>
						<div className="relative flex flex-col gap-4 p-6 md:p-14">
							<h1 className="max-w-[546px] text-4xl leading-[1.143] font-medium tracking-[-0.02em] text-white md:text-[3.5rem]">
								{t`A story with social impact`}
							</h1>
							<p className="max-w-[584px] text-base leading-[1.5] text-white">
								{t`What if we could transform the world every time we use our card?`}
							</p>
						</div>
					</div>
				</Expand>

				<div className="flex flex-col gap-6">
					{/* #FFEDDF is not `peach` (#f8ddc9). The artboard is cropped to the drawing, which `w-[77%]` is sized for. */}
					<Expand index={1} asChild>
						<div className="flex items-center justify-center overflow-hidden rounded-3xl bg-[#FFEDDF] lg:h-[373px]">
							<AppImpactIllustration label={t`The Worldcoo app showing a donor's impact`} className="h-auto w-[77%]" />
						</div>
					</Expand>

					<Expand index={1.5} asChild>
						<div className="bg-ink flex flex-col justify-between gap-8 rounded-3xl p-8 lg:h-[248px] lg:px-10">
							<Zap className="size-8 shrink-0 fill-current text-white" strokeWidth={0} />
							<div className="flex flex-col">
								<p className="text-olive text-5xl leading-[1.3] font-medium tabular-nums md:text-[4rem]">
									<Suspense fallback={<span className="opacity-40">—</span>}>
										<ProjectsFunded />
									</Suspense>
								</p>
								<p className="text-xl leading-[1.5] font-medium text-white/70 md:text-2xl">{t`Funded projects`}</p>
							</div>
						</div>
					</Expand>
				</div>
			</section>
		</ExpandGroup>
	);
}
